import { BankOperationStatusesNew } from 'constants/bankOperationStatusesNew';

import { Card } from '@mui/material';
import { useUpdateBankOperationsStatus } from 'pages/Finance/BankOperations/hooks/useUpdateBankOperationsStatus';
import { queryClient } from 'providers/queryClient';
import { maxLength, required, useNotify } from 'react-admin';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog, DialogProps } from 'shared/mui/Dialogs/Dialog/Dialog';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { TextInput } from 'shared/react-admin/Inputs';
import { BankOperation } from 'types/embedded/bankOperation';
import { getId } from 'utils/getId';

type Props = {
  record: BankOperation | undefined;
} & DialogProps;

type FormValues = {
  comment: string;
};

export const BankOperationsShowCancelDialog: React.FC<Props> = ({ record, ...restProps }) => {
  const form = useForm<FormValues>();
  const notify = useNotify();
  const { mutate: updateBankOperationsStatus } = useUpdateBankOperationsStatus();

  const onClose = () => {
    form.reset();
    restProps.onClose();
  };

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    updateBankOperationsStatus(
      {
        id: getId(record?.id),
        data: {
          status: BankOperationStatusesNew.Canceled,
          comment: values?.comment,
        },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries();

          notify(`Банковская операция ID ${getId(record?.id)} переведена в статус Аннулирована`, {
            type: 'success',
          });
          onClose();
        },
        onError: (error: any) => notify(error?.data?.violations[0]?.message, { type: 'error' }),
      },
    );
  };

  return (
    <Dialog {...restProps} width="640px">
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <CardHeader
              appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
              title="Подтвердить запрос на аннулирование?"
              titleSize="smallBold"
            />
            <CardContent>
              <Typography>
                После аннулирования банковская операция будет отменена и недоступна для изменений.
                <br />
                <br />
                Перед аннулированием обязательно оставьте комментарий.
              </Typography>
              <Box sx={{ marginTop: '24px' }}>
                <TextInput
                  fullWidth={true}
                  helperText={false}
                  label="Комментарий"
                  multiline={true}
                  resettable
                  size="small"
                  source="comment"
                  validate={[
                    required(),
                    maxLength(
                      1024,
                      'Это значение слишком длинное. Оно должно содержать 1024 символа или меньше.',
                    ),
                  ]}
                  variant="outlined"
                />
              </Box>
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <CancelButton
                  key="back-button"
                  label="Отменить"
                  onClick={onClose}
                  variant="outlined"
                />,
                <Button
                  color="error"
                  key="ok-button"
                  label="Подтвердить"
                  type="submit"
                  variant="contained"
                />,
              ]}
            />
          </Card>
        </form>
      </FormProvider>
    </Dialog>
  );
};
